import './style.scss';

import cn from 'classnames';
import isNil from 'lodash/isNil';
import React, { useCallback } from 'react';

import { BaseSelectField } from '../base/BaseSelectField';

/**
 * Highlights matching text by wrapping it in a span with highlight class.
 * Case-insensitive matching.
 */
function highlightText(text: string, searchTerm: string): React.ReactNode {
  if (!searchTerm.trim()) return text;

  const regex = new RegExp(`(${searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
  const parts = text.split(regex);

  return parts.map((part, index) =>
    regex.test(part) ? (
      <span key={index} className="mm-select-field__highlight">
        {part}
      </span>
    ) : (
      part
    ),
  );
}

interface ISelectFieldProps<V> {
  items: V[];
  itemToString: (item: V) => string;
  selectedItem?: V;
  onSelectItem: (item: V | undefined) => void;
  label?: string;
  placeholder?: string;
  disabled?: boolean;
  errorMessage?: string;
  comment?: React.ReactNode;
  className?: string;
  id?: string;
  /** Optional filter function to enable search. When provided, input becomes editable. */
  filterFunction?: (item: V, searchTerm: string) => boolean;
}

function SelectField<V>({
  items,
  itemToString,
  selectedItem,
  onSelectItem,
  label,
  placeholder,
  errorMessage,
  comment,
  className,
  disabled = false,
  id,
  filterFunction,
}: ISelectFieldProps<V>) {
  const itemRenderer = useCallback(
    (item: V, searchTerm?: string) => {
      const itemString = itemToString(item);

      const isItemSelected = !isNil(selectedItem) && itemString === itemToString(selectedItem);

      // Highlight search term if present
      const content = searchTerm ? highlightText(itemString, searchTerm) : itemString;

      return (
        <div className={cn('mm-select-field__option', { _selected: isItemSelected })}>
          {content}
        </div>
      );
    },
    [itemToString, selectedItem],
  );

  return (
    <BaseSelectField
      id={id}
      className={cn('mm-select-field', className)}
      items={items}
      onSelectItem={onSelectItem}
      selectedItem={selectedItem}
      selectedItemText={!isNil(selectedItem) ? itemToString(selectedItem) : undefined}
      renderItem={itemRenderer}
      disabled={disabled}
      label={label}
      placeholder={placeholder}
      errorMessage={errorMessage}
      comment={comment}
      filterFunction={filterFunction}
    />
  );
}

export { SelectField };
